import { useState } from "react";
import { ChevronDown } from "lucide-react";
import VerificationLabel from "@/components/VerificationLabel";

/**
 * Roadmap card — shows a chronological pathway with expandable stage details.
 *
 * Props:
 *   title, subtitle, stages: [{ semester_label, milestones: [str], notes }]
 *   status ("verified"|"awaiting"|...)
 */
export default function RoadmapCard({ title, subtitle, stages = [], status = "awaiting", href, className = "" }) {
  const [expanded, setExpanded] = useState(null);
  return (
    <div className={`border border-stone/40 bg-bone p-6 sm:p-8 ${className}`} data-testid="roadmap-card">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="font-serif text-2xl text-ivy tracking-tight">{title}</h3>
          {subtitle && <p className="text-xs uppercase tracking-[0.18em] text-brass mt-1">{subtitle}</p>}
        </div>
        <VerificationLabel status={status} />
      </div>
      <ol className="border-l-2 border-stone/30 pl-5 space-y-4 mt-6">
        {stages.map((s, i) => {
          const open = expanded === i;
          return (
            <li key={i} className="relative">
              <span className="absolute -left-[27px] top-1 h-3 w-3 border-2 border-brass bg-bone" />
              <button
                type="button"
                onClick={() => setExpanded(open ? null : i)}
                className="w-full text-left flex items-baseline justify-between gap-3 group"
                data-testid={`roadmap-stage-${i}`}
              >
                <span className="text-[11px] uppercase tracking-[0.22em] text-brass font-semibold">{s.semester_label}</span>
                <ChevronDown className={`h-3.5 w-3.5 text-stone transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              {open && s.milestones && s.milestones.length > 0 && (
                <ul className="mt-3 space-y-1.5 text-[13px] text-ink-soft leading-relaxed">
                  {s.milestones.map((m, j) => <li key={j} className="flex gap-2"><span className="text-brass mt-2 h-px w-3 bg-brass flex-shrink-0" /><span>{m}</span></li>)}
                </ul>
              )}
              {open && s.notes && <p className="mt-2 text-[12px] text-stone italic">{s.notes}</p>}
            </li>
          );
        })}
      </ol>
      {href && (
        <a href={href} className="mt-6 inline-block text-[11px] uppercase tracking-[0.18em] font-semibold text-ivy hover:text-brass">
          Open full roadmap →
        </a>
      )}
    </div>
  );
}
